<script>
  import { Carousel, Slide } from 'vue3-carousel';
  import { toggleZoom, getMediaAlt } from '@/lib/vue-helpers.js';
  import { emitter } from '@/main.js';
  import pinchZoom from '@/lib/pinchZoom.js';

  export default {
    name: 'PDPZoom',
    components: {
      Carousel,
      Slide
    },
    data() {
      return {
        images: [],
        activeSlide: 0,
        isMobileSize: false,
        pinchHandlers: null,
        viewportPinRaf: null,
        carouselConfig: {
          transition: 0,
          wrapAround: true,
          mouseDrag: false,
          breakpoints: {
            786: {
              wrapAround: false,
              dir: 'ttb',
              gap: 20
            }
          }
        }
      };
    },
    computed: {
      showPaginationAndNavigation() {
        return this.isMobileSize && this.images.length > 1;
      },
      deviceType() {
        return window.siteGlobals?.device;
      }
    },
    mounted() {
      this.isMobileSize = this.deviceType === 'mobile-size';
      this.pinchHandlers = pinchZoom();
      this.setupEventListeners();

      this.$nextTick(() => {
        this.setupPinchZoom();
      });
    },
    beforeUnmount() {
      this.cleanup();
    },
    methods: {
      getMediaAlt,
      setupEventListeners() {
        document.addEventListener('theme:resize', this.handleResize);

        const zoomWrapper = document.getElementById('vue-zoom-wrapper');
        if (zoomWrapper) {
          zoomWrapper.addEventListener('click', this.handleBackgroundClick);
        }

        document.addEventListener('pdp-zoom:open', this.startViewportPin);
        document.addEventListener('pdp-zoom:close', this.stopViewportPin);

        emitter.on('pdp-color-updated', this.handleColorUpdate);
        emitter.on('active-product-slide-for-zoom', this.handleActiveProductSlide);
      },
      handleColorUpdate(e) {
        this.images = e.images;
        this.activeSlide = 0;
        this.$nextTick(() => {
          this.setupPinchZoom();
        });
      },
      handleActiveProductSlide(productImageUrl) {
        if (!productImageUrl) return;
        const stripParams = url => url?.split('?')[0] || '';
        const target = stripParams(productImageUrl);
        const index = this.images.findIndex(image => stripParams(image.node.url) === target);
        if (index === -1) return;

        this.activeSlide = index;

        if (!this.$refs.pdpZoomCarousel) return;

        if (this.isMobileSize) {
          this.$refs.pdpZoomCarousel.slideTo(index);
        } else {
          this.$nextTick(() => {
            this.scrollToSlide(index);
          });
        }
      },
      scrollToSlide(index) {
        const carousel = this.$refs.pdpZoomCarousel?.$el;
        const slides = carousel?.querySelectorAll('.carousel__slide');

        if (carousel && slides?.[index]) {
          slides[index].scrollIntoView({
            behavior: 'instant',
            block: 'start'
          });
        }
      },
      handleZoom() {
        toggleZoom();
      },
      handleActiveSlideIndex(direction) {
        // Wrap around for mobile size
        if (this.isMobileSize) {
          if (direction === 'prev') {
            if (this.activeSlide === 0) {
              this.activeSlide = this.images.length - 1;
            } else {
              this.activeSlide = this.activeSlide - 1;
            }
          } else if (direction === 'next') {
            if (this.activeSlide === this.images.length - 1) {
              this.activeSlide = 0;
            } else {
              this.activeSlide = this.activeSlide + 1;
            }
          }
        } else {
          if (direction === 'prev') {
            this.activeSlide = Math.max(0, this.activeSlide - 1);
          } else if (direction === 'next') {
            this.activeSlide = Math.min(this.images.length - 1, this.activeSlide + 1);
          }
        }
      },
      handleDoubleZoom(event) {
        if (this.isMobileSize) return;

        event.stopPropagation();

        const slide = event.currentTarget.closest('.carousel__slide');
        slide?.classList.toggle('zoomed');
      },
      handleResize() {
        const newDeviceType = this.deviceType;
        const wasMobile = this.isMobileSize;
        this.isMobileSize = newDeviceType === 'mobile-size';

        if (wasMobile !== this.isMobileSize) {
          this.$nextTick(() => {
            if (this.$refs.pdpZoomCarousel) {
              this.$refs.pdpZoomCarousel.restartCarousel();
              this.setupPinchZoom();
            }
          });
        }
      },
      handleBackgroundClick(event) {
        if (event.target.id !== 'vue-zoom-wrapper') return;

        const zoomContent = document.getElementById('vue-zoom');
        if (zoomContent && !zoomContent.contains(event.target)) {
          this.handleZoom();
        }
      },
      setupPinchZoom() {
        const shouldAddListeners = this.isMobileSize && this.$refs.pdpZoomCarousel && this.pinchHandlers;
        this.managePinchListeners(shouldAddListeners ? 'add' : 'remove');
      },
      managePinchListeners(action) {
        if (!this.$refs.pdpZoomCarousel || !this.pinchHandlers) return;

        const carouselEl = this.$refs.pdpZoomCarousel.$el;
        const events = ['touchstart', 'touchmove', 'touchend'];

        events.forEach(event => {
          carouselEl[`${action}EventListener`](event, this.pinchHandlers[event]);
        });
      },
      startViewportPin() {
        if (!this.isMobileSize || !window.visualViewport) return;
        window.visualViewport.addEventListener('resize', this.scheduleViewportPin);
        window.visualViewport.addEventListener('scroll', this.scheduleViewportPin);
        this.syncViewportPin();
      },
      scheduleViewportPin() {
        if (this.viewportPinRaf) return;
        this.viewportPinRaf = window.requestAnimationFrame(() => {
          this.viewportPinRaf = null;
          this.syncViewportPin();
        });
      },
      syncViewportPin() {
        const wrapper = document.getElementById('vue-zoom-wrapper');
        const vv = window.visualViewport;
        if (!wrapper || !vv) return;
        wrapper.style.width = `${vv.width * vv.scale}px`;
        wrapper.style.height = `${vv.height * vv.scale}px`;
        wrapper.style.transformOrigin = '0 0';
        wrapper.style.transform = `translate(${vv.offsetLeft}px, ${vv.offsetTop}px) scale(${1 / vv.scale})`;
      },
      stopViewportPin() {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', this.scheduleViewportPin);
          window.visualViewport.removeEventListener('scroll', this.scheduleViewportPin);
        }
        if (this.viewportPinRaf) {
          window.cancelAnimationFrame(this.viewportPinRaf);
          this.viewportPinRaf = null;
        }
        const wrapper = document.getElementById('vue-zoom-wrapper');
        if (wrapper) {
          wrapper.style.width = '';
          wrapper.style.height = '';
          wrapper.style.transform = '';
          wrapper.style.transformOrigin = '';
        }
      },
      cleanup() {
        document.removeEventListener('theme:resize', this.handleResize);
        document.removeEventListener('pdp-zoom:open', this.startViewportPin);
        document.removeEventListener('pdp-zoom:close', this.stopViewportPin);

        const zoomWrapper = document.getElementById('vue-zoom-wrapper');
        if (zoomWrapper) {
          zoomWrapper.removeEventListener('click', this.handleBackgroundClick);
        }

        this.stopViewportPin();

        if (this.$refs.pdpZoomCarousel && this.pinchHandlers) {
          this.managePinchListeners('remove');
        }

        emitter.off('pdp-color-updated', this.handleColorUpdate);
        emitter.off('active-product-slide-for-zoom', this.handleActiveProductSlide);
      }
    }
  };
</script>

<template>
  <template v-if="images.length">
    <div class="close-wrapper">
      <button class="pswp__button pswp__button--close" title="Close (Esc)" @click="handleZoom">
        <svg v-if="!isMobileSize" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.625" y="0.625" width="48.75" height="48.75" rx="24.375" fill="white" fill-opacity="0.75" />
          <rect x="0.625" y="0.625" width="48.75" height="48.75" rx="24.375" stroke="#E0E0E0" stroke-width="1.25" />
          <path d="M12.5 12.5L37.5 37.5M12.5 37.5L37.5 12.5" stroke="#737373" stroke-width="1.25" />
        </svg>
        <svg v-else aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0.625" y="0.625" width="38.75" height="38.75" rx="19.375" fill="white" fill-opacity="0.75" />
          <rect x="0.625" y="0.625" width="38.75" height="38.75" rx="19.375" stroke="#E0E0E0" stroke-width="1.25" />
          <path d="M10 10L30 30M10 30L30 10" stroke="#737373" stroke-width="1.25" />
        </svg>
      </button>
    </div>
    <carousel ref="pdpZoomCarousel" v-bind="carouselConfig" v-model="activeSlide">
      <slide v-for="(product, index) in images" :key="'zoom-pics-' + index" @click="handleDoubleZoom">
        <div class="zoom-image-wrapper">
          <img :src="product.node.url" class="slideShow" :alt="getMediaAlt(product)" />
        </div>
      </slide>
    </carousel>
    <div class="zoom-controls" v-if="showPaginationAndNavigation">
      <button @click="handleActiveSlideIndex('prev')">
        <svg aria-label="Show Previous Slide" width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.5 0.5L1 6L6.5 11.5" stroke="#151515" stroke-width="1.25" />
        </svg>
      </button>
      <span class="gt-p__small" style="color: #151515">{{ activeSlide + 1 }} / {{ images.length }}</span>
      <button @click="handleActiveSlideIndex('next')">
        <svg aria-label="Show Next Slide" width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0.5 11.5L6 6L0.5 0.5" stroke="#151515" stroke-width="1.25" />
        </svg>
      </button>
    </div>
  </template>
</template>
